import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Footer } from '../components/Footer';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicPageMeta } from '../seo/publicPageMeta';
import { trackPageView } from '../services/telemetry';

const linkClass = 'text-orange-600 underline hover:text-blue-800';

interface TermsSectionProps {
  sectionNumber: number;
  title: string;
  children: React.ReactNode;
}

function TermsSection({ sectionNumber, title, children }: TermsSectionProps) {
  return (
    <section
      id={`terms-section-${sectionNumber}`}
      className="scroll-mt-6"
      aria-labelledby={`terms-heading-${sectionNumber}`}
    >
      <div className="mb-4 flex items-center gap-3">
        <span
          className={`
            flex h-10 w-10 shrink-0 items-center justify-center rounded-full
            bg-yellow-400 text-lg font-bold text-gray-800
          `}
          aria-hidden="true"
        >
          {sectionNumber}
        </span>
        <h2
          id={`terms-heading-${sectionNumber}`}
          className="text-xl font-bold text-gray-800 sm:text-2xl"
        >
          {title}
        </h2>
      </div>
      <div className="ml-[52px] space-y-4 text-gray-600">{children}</div>
    </section>
  );
}

function TermsOfService() {
  usePageMeta(getPublicPageMeta('/terms-of-service'));

  useEffect(() => {
    trackPageView('TermsOfService', '/terms-of-service');
  }, []);

  return (
    <>
      <main
        className={`
          bg-gray-25 flex min-h-screen flex-col items-center p-4 font-[Outfit]
        `}
      >
        <header className="mb-10 w-full max-w-6xl text-center">
          <p className="mb-2 text-sm font-medium text-yellow-600">
            PineappleMusicLab
          </p>
          <h1
            className={`
              mb-3 text-3xl font-bold text-gray-800
              sm:text-4xl
              lg:text-5xl
            `}
          >
            Terms of Service
          </h1>
          <p className="text-base text-gray-600 sm:text-lg">
            Effective Date: May 2, 2026 &nbsp;|&nbsp; Last Updated: November 2,
            2026
          </p>
        </header>

        <article className="mb-12 flex w-full max-w-6xl flex-col gap-12">
          <div className="space-y-4 text-gray-600">
            <p>
              Welcome to PineappleMusicLab. These Terms of Service
              (&quot;Terms&quot;) govern your access to and use of the
              PineappleMusicLab website and mobile application (collectively,
              the &quot;Service&quot;), operated by PineappleMusicLab
              (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
            </p>
            <p>
              By creating an account or using the Service, you agree to be bound
              by these Terms. If you do not agree, do not use the Service.
            </p>
          </div>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={1} title="Eligibility">
            <p>
              The Service is intended for users aged 13 and older. By
              registering an account, you represent that you are at least 13
              years of age. If you are between 13 and 18, you represent that a
              parent or legal guardian has reviewed and agrees to these Terms on
              your behalf.
            </p>
            <p>
              Accounts may not be registered to anyone under 13. A parent or
              legal guardian may use their own account to allow a younger family
              member to practice with the Service; in that case, the account
              holder remains the registered user and is solely responsible for
              all activity on the account, including any activity by family
              members.
            </p>
            <p>
              We are not required to verify your age, and you are solely
              responsible for ensuring your eligibility.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={2} title="Description of the Service">
            <p>
              PineappleMusicLab is an AI-powered tool for piano practice. You
              upload sheet music in MusicXML, PDF, or image format, and the
              Service generates piano fingering suggestions and other
              practice-assisting annotations overlaid on your sheet music.
            </p>
            <div
              className={`
                mt-4 rounded-r-lg border-l-4 border-yellow-400 bg-yellow-50 p-5
                text-gray-800 shadow-sm
              `}
            >
              <p className="mb-0 text-sm sm:text-base">
                <strong>Important:</strong> The fingerings and annotations
                generated by PineappleMusicLab are AI-generated suggestions
                intended as practice aids. They do not constitute professional
                music instruction, and we do not guarantee their accuracy or
                suitability for any individual. Always use your own musical
                judgment and consult a qualified instructor if needed.
              </p>
            </div>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={3} title="Your Account">
            <p>
              Certain features of the Service require you to create an account.
              Some features may be accessible without an account. If you create
              an account, you are responsible for maintaining the
              confidentiality of your login credentials and for all activity
              that occurs under your account. You agree to notify us immediately
              at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>{' '}
              if you suspect unauthorized use of your account. Whether or not
              you create an account, your use of the Service is subject to these
              Terms.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={4} title="User Content and Conduct">
            <h3 className="text-base font-semibold text-gray-800">
              Your Uploads
            </h3>
            <p>
              You may upload sheet music files to the Service for processing. By
              uploading content, you represent and warrant that you have the
              necessary rights or permissions to upload and use that content
              with the Service. You are solely responsible for ensuring that
              your use of uploaded material does not infringe the intellectual
              property rights of any third party.
            </p>
            <p>
              PineappleMusicLab is not obligated to monitor or verify the
              copyright status of uploaded content. However, we reserve the
              right to remove content that we reasonably believe infringes on
              third-party rights, in accordance with applicable law.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              License to Us
            </h3>
            <p>
              By uploading content, you grant PineappleMusicLab a non-exclusive,
              worldwide, royalty-free license to use, process, and store that
              content for the purpose of providing and improving the Service,
              including training and improving our AI models. You may opt out of
              the AI training use by contacting us at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>
              .
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Prohibited Conduct
            </h3>
            <p>You agree not to:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                Reverse engineer, decompile, disassemble, or otherwise attempt
                to discover the source code or algorithms of the Service or its
                AI models.
              </li>
              <li>
                Use automated tools (bots, scrapers, crawlers) to access or
                extract data from the Service, unless expressly authorized
                through a separate API agreement with PineappleMusicLab.
              </li>
              <li>
                Interfere with or disrupt the integrity or performance of the
                Service.
              </li>
              <li>
                Use the Service for any unlawful purpose or in violation of
                these Terms.
              </li>
              <li>
                Attempt to gain unauthorized access to any part of the Service
                or its related systems.
              </li>
              <li>
                Resell, sublicense, or redistribute the Service or its output
                without our prior written permission or a separate commercial
                agreement.
              </li>
            </ul>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={5} title="Intellectual Property">
            <h3 className="text-base font-semibold text-gray-800">
              Our Property
            </h3>
            <p>
              The Service, including its AI models, algorithms, software,
              design, and branding, are the intellectual property of
              PineappleMusicLab or its licensors and are protected by applicable
              intellectual property laws.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Your Property
            </h3>
            <p>
              You retain ownership of the original sheet music content you
              upload to the Service, subject to the license granted in Section
              4. We do not claim ownership of your original uploaded files.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Generated Output
            </h3>
            <p>
              The AI-generated annotations, fingerings, and processed outputs
              produced by the Service are yours to use freely, including for
              personal practice, teaching, publishing, and other commercial
              purposes. We do not claim ownership of the output generated from
              your uploaded content.
            </p>
            <p>
              However, you may not use the generated outputs to build or
              contribute to a competing product or service (for example,
              compiling outputs into a dataset to train a rival AI model or
              creating a standalone fingering database for resale). The
              underlying technology, models, and methods used to generate the
              output remain the exclusive property of PineappleMusicLab.
            </p>
            <p>
              In particular, you may not use generated outputs, including the
              fingering annotations in them, to train, fine-tune, distill, or
              evaluate machine-learning models that compete with
              PineappleMusicLab, or allow others to do so. This applies to
              outputs obtained through the website, the mobile app, and the
              Business API. Downloaded and API output files carry an embedded
              notice of this restriction; removing the notice does not remove
              the restriction.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={6} title="Subscriptions and Payments">
            <h3 className="text-base font-semibold text-gray-800">
              Subscription Plans
            </h3>
            <p>
              Certain features of the Service require a paid subscription.
              Details about available plans and pricing are displayed within the
              Service and on our website.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Web Purchases
            </h3>
            <p>
              Subscriptions purchased through our website are processed by
              Stripe. By subscribing, you authorize Stripe to charge your chosen
              payment method on a recurring basis (monthly or annually,
              depending on your plan) until you cancel. You can manage or cancel
              your subscription at any time through our website.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Mobile App Purchases
            </h3>
            <p>
              Subscriptions purchased through the iOS App Store or Google Play
              Store are processed by Apple or Google, respectively, and are
              subject to their terms and billing policies. Subscription
              management, cancellation, and refund requests for mobile purchases
              must be handled through your Apple ID or Google Play account
              settings.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Refunds
            </h3>
            <p>
              For web purchases via Stripe, refund requests are handled on a
              case-by-case basis. Contact us at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>
              . For mobile app purchases, refund policies are governed by Apple
              or Google&apos;s respective refund policies.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Price Changes
            </h3>
            <p>
              We may change our subscription pricing from time to time. We will
              provide you with reasonable advance notice of any price changes.
              Continued use of the Service after a price change takes effect
              constitutes your acceptance of the new pricing.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={7} title="Disclaimers">
            <p className="font-semibold text-gray-800">
              THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS
              AVAILABLE&quot; BASIS, WITHOUT WARRANTIES OF ANY KIND, WHETHER
              EXPRESS, IMPLIED, OR STATUTORY. TO THE FULLEST EXTENT PERMITTED BY
              LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO
              IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>Without limiting the foregoing, we do not warrant that:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>The Service will be uninterrupted, error-free, or secure.</li>
              <li>
                The AI-generated fingerings, annotations, or other outputs will
                be accurate, complete, or suitable for your needs.
              </li>
              <li>Any defects in the Service will be corrected.</li>
            </ul>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={8} title="Limitation of Liability">
            <p className="font-semibold text-gray-800">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
              SHALL PINEAPPLEMUSICLAB, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR
              AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO
              LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR
              USE OF THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT
              (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, EVEN IF WE HAVE
              BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p>
              Our total cumulative liability to you for all claims arising out
              of or related to these Terms or the Service shall not exceed the
              amount you have paid to PineappleMusicLab in the twelve (12)
              months preceding the claim.
            </p>
            <div
              className={`
                mt-4 rounded-r-lg border-l-4 border-yellow-400 bg-yellow-50 p-5
                text-gray-800 shadow-sm
              `}
            >
              <p className="mb-0 text-sm sm:text-base">
                <strong>Physical injury disclaimer:</strong> PineappleMusicLab
                provides AI-generated practice suggestions only. We are not
                responsible for any physical injury, strain, or discomfort that
                may occur while practicing piano or any other instrument. Always
                practice responsibly and consult a qualified instructor or
                medical professional if you experience pain or discomfort.
              </p>
            </div>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={9} title="Indemnification">
            <p className="font-semibold text-gray-800">
              You agree to indemnify, defend, and hold harmless
              PineappleMusicLab and its officers, directors, employees, and
              agents from and against any claims, damages, losses, liabilities,
              and expenses (including reasonable attorneys&apos; fees) arising
              out of or related to your use of the Service, your violation of
              these Terms, or your infringement of any third-party rights,
              including intellectual property rights related to content you
              upload.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={10} title="Account Termination">
            <h3 className="text-base font-semibold text-gray-800">By You</h3>
            <p>
              You may delete your account at any time using the in-app account
              deletion feature or by contacting us at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>
              . Upon deletion, your data will be removed from our systems as
              described in our{' '}
              <Link to="/privacy-policy" className={linkClass}>
                Privacy Policy
              </Link>
              .
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              By Us
            </h3>
            <p>
              We reserve the right to suspend or terminate your account at any
              time, with or without notice, for conduct that we determine, in
              our sole discretion, violates these Terms, is harmful to other
              users or the Service, or is otherwise objectionable. Upon
              termination, your right to use the Service will immediately cease.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={11} title="Governing Law">
            <p>
              These Terms shall be governed by and construed in accordance with
              the laws of the State of Utah, United States, without regard to
              its conflict of law provisions.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={12} title="Dispute Resolution">
            <h3 className="text-base font-semibold text-gray-800">
              Informal Resolution
            </h3>
            <p>
              Before filing any formal proceeding, you agree to first contact us
              at{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>{' '}
              and attempt to resolve the dispute informally for at least thirty
              (30) days.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Binding Arbitration
            </h3>
            <p>
              If we cannot resolve the dispute informally, you and
              PineappleMusicLab agree to resolve any claims relating to these
              Terms or the Service through final and binding arbitration,
              administered by the American Arbitration Association
              (&quot;AAA&quot;) under its Consumer Arbitration Rules. The
              arbitration will be conducted in Salt Lake City, Utah, or remotely
              at your election.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Class Action Waiver
            </h3>
            <p className="font-semibold text-gray-800">
              YOU AND PINEAPPLEMUSICLAB AGREE THAT EACH MAY BRING CLAIMS AGAINST
              THE OTHER ONLY IN YOUR OR OUR INDIVIDUAL CAPACITY, AND NOT AS A
              PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR
              REPRESENTATIVE PROCEEDING.
            </p>

            <h3 className="mt-6 text-base font-semibold text-gray-800">
              Small Claims Exception
            </h3>
            <p>
              Notwithstanding the above, either party may bring an individual
              action in small claims court in Salt Lake County, Utah, provided
              the claim falls within the court&apos;s jurisdictional limits.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={13} title="Changes to These Terms">
            <p>
              We may update these Terms from time to time. When we make material
              changes, we will notify you by email and update the &quot;Last
              Updated&quot; date at the top of this page. Your continued use of
              the Service after the updated Terms become effective constitutes
              your acceptance of the changes. If you do not agree to the updated
              Terms, you must stop using the Service.
            </p>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={14} title="General Provisions">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-gray-800">Entire Agreement:</strong>{' '}
                These Terms, together with our{' '}
                <Link to="/privacy-policy" className={linkClass}>
                  Privacy Policy
                </Link>
                , constitute the entire agreement between you and
                PineappleMusicLab regarding the Service.
              </li>
              <li>
                <strong className="text-gray-800">Severability:</strong> If any
                provision of these Terms is found to be unenforceable, the
                remaining provisions will remain in full force and effect.
              </li>
              <li>
                <strong className="text-gray-800">Waiver:</strong> Our failure
                to enforce any right or provision of these Terms will not be
                deemed a waiver of that right or provision.
              </li>
              <li>
                <strong className="text-gray-800">Assignment:</strong> You may
                not assign or transfer these Terms without our prior written
                consent. We may assign our rights and obligations without
                restriction.
              </li>
            </ul>
          </TermsSection>

          <div className="h-px w-full bg-gray-200" aria-hidden="true" />

          <TermsSection sectionNumber={15} title="Contact Us">
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <p className="text-gray-800">
              <strong>PineappleMusicLab</strong>
              <br />
              Email:{' '}
              <a
                href="mailto:hello@pineapplemusiclab.com"
                className={linkClass}
              >
                hello@pineapplemusiclab.com
              </a>
            </p>
          </TermsSection>
        </article>
      </main>

      <Footer />
    </>
  );
}

export { TermsOfService };
